<template>
  <div class="@container flex w-full flex-col gap-3">
    <div
      v-if="showDangerousGoods"
      class="bg-bgr-secondary border-bdr-default col-span-full flex cursor-pointer flex-col items-start gap-1 rounded-xl border"
      @click="toggleDangerous"
    >
      <div class="flex items-center gap-3 p-2">
        <Toggle v-model="line.dangerous_goods_set" @click.stop="" />
        <span class="text-body-default-m">{{ t("order_lines.dangerous_goods_header") }}</span>
      </div>
    </div>
    <Divider v-if="showDangerousGoods" />
    <div class="flex w-full flex-col gap-1.5">
      <InputText
        v-model="line.customs[0].description"
        class=""
        is-required
        type="text"
        :placeholder="t('eg_computer_screens')"
        :label="t('item_description')"
        :readonly="disabled"
        :error="errors.description"
        @blur="() => validateField('description', line.customs[0].description, t('item_description'))"
      />
      <span class="flex gap-3">
        <InputNumber
          v-model="line.qty"
          class="max-w-[100px]"
          is-required
          :label="t('qty')"
          :readonly="disabled"
          :error="errors.qty"
          @blur="() => validateField('qty', line.qty, t('qty'))"
        />
        <InputText
          class="w-full"
          v-model="line.item_number"
          is-required
          :label="t('product_number')"
          :readonly="disabled"
          :error="errors.item_number"
          @blur="() => validateField('item_number', line.item_number, t('product_number'))"
        />
      </span>
      <div>
        <InputUnit
          v-model:unit="line.customs[0].currency_code"
          v-model:value="line.customs[0].total_price"
          :options="currencies"
          :label="t('total_price')"
          type="number"
          :step="0.01"
          :readonly="disabled"
          unit-filterable
          :trigger-text="t('select_currency')"
        />
        <Message v-if="errors.total_price || errors.currency_code" variant="invalid">
          {{ errors.total_price || errors.currency_code }}
        </Message>
      </div>
      <div class="flex w-full flex-col gap-1.5">
        <span class="flex w-full gap-3">
          <DropdownCountry
            v-model="line.customs[0].origin_country"
            :label="t('origin_country')"
            :readonly="disabled"
            :error="errors.origin_country"
            class="min-w-1/2"
          />
          <InputText
            v-model="line.customs[0].sender_tariff"
            :label="t('tariff_number')"
            class="w-1/2"
            :readonly="disabled"
            :modifiers="['numeric']"
            :error="errors.sender_tariff"
            @update:model-value="(val) => (line.customs[0].receiver_tariff = val?.toString())"
            @blur="() => ('sender_tariff', line.customs[0].sender_tariff, t('tariff_number'))"
          />
        </span>
        <InputNumber
          v-model="formattedWeight"
          :label="t('total_weight')"
          :min="0"
          :step="1"
          :suffix="displayWeightUnit"
          :error="errors.weight"
          @blur="() => validateField('weight', line.customs[0].weight, t('weight'))"
        />
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
  import type { IOrderline } from "~/types/orderline";

  const { t } = useI18n();
  const { generateDangerousGoods } = useOrderlines();
  const displayWeightUnit = ref(usePreferredWeightUnit());

  // Validation errors
  const errors = reactive({
    currency_code: "",
    description: "",
    item_number: "",
    origin_country: "",
    qty: "",
    sender_tariff: "",
    total_price: "",
    weight: "",
  });

  // Validation functions
  const validateField = (field: keyof typeof errors, value: unknown, label: string) => {
    if (!value || value === "" || value === null || value === undefined) {
      errors[field] = t("is_required_field", { entity: label });
    } else {
      errors[field] = "";
    }
  };

  const line = defineModel<IOrderline>({
    required: true,
  });

  const {
    units,
    currencies,
    disabled = undefined,
    senderCountry = "DK",
    receiverCountry = "DK",
    isCrossBorder = false,
    type = undefined,
    showDangerousGoods,
    missingFields,
  } = defineProps<{
    units: { label: string; value: string }[];
    currencies: { label: string; value: string }[];
    disabled?: boolean;
    senderCountry?: string | "DK";
    receiverCountry?: string | "DK";
    isCrossBorder?: boolean | false;
    type?: "shipment" | "consignment";
    showDangerousGoods?: boolean | true;
    missingFields?: string[];
  }>();

  const missingFieldLabelKeys: Record<string, string> = {
    description: "item_description",
    qty: "qty",
    item_number: "product_number",
    total_price: "total_price",
    currency_code: "currency_code",
    origin_country: "origin_country",
    sender_tariff: "tariff_number",
    weight: "weight",
  };

  onMounted(() => {
    missingFields?.forEach((field) => {
      if (field in errors && field in missingFieldLabelKeys) {
        errors[field as keyof typeof errors] = t("is_required_field", { entity: t(missingFieldLabelKeys[field]) });
      }
    });
  });

  const formattedWeight = computed({
    get: () => {
      if (!line.value.customs[0].weight) return null;

      return convertWeight(line.value.customs[0].weight, "g", displayWeightUnit.value);
    },
    set: (val) => {
      if (!line.value.customs) return;
      // This is where the weight value is set and we must always convert it to grams
      line.value.customs[0].weight = convertWeight(val, displayWeightUnit.value, "g");
      line.value.customs[0].unit = displayWeightUnit.value;
    },
  });

  const toggleDangerous = () => {
    if (line.value.dangerous_goods_set) {
      line.value.dangerous_goods = [];
      line.value.dangerous_goods_set = false;
      return;
    }
    line.value.dangerous_goods_set = true;
    line.value.dangerous_goods = [];
    line.value.dangerous_goods.push(generateDangerousGoods());
  };

  watch(
    () => line.value.dangerous_goods_set,
    (newVal) => {
      if (newVal && line.value.dangerous_goods) {
        line.value.dangerous_goods = [];
        line.value.dangerous_goods.push(generateDangerousGoods());
      }
    }
  );
</script>
